import { useMemo, useState } from "react";
import Select from "./Select";
import type { Calculator, CalcField, CalcValues } from "@/lib/calculators/types";

const clamp = (n: number, field: CalcField) => {
  let v = n;
  if (field.min != null) v = Math.max(field.min, v);
  if (field.max != null) v = Math.min(field.max, v);
  return v;
};

function Field({
  field,
  value,
  resetValue,
  onChange,
}: {
  field: CalcField;
  value: number;
  resetValue: number;
  onChange: (v: number) => void;
}) {
  const hasSlider = field.type !== "select" && field.min != null && field.max != null;
  const sliderValue = Number.isFinite(value) ? value : field.min;
  const showReset =
    field.resettable && Number.isFinite(resetValue) && (!Number.isFinite(value) || value !== resetValue);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={field.key} className="text-[13.5px] font-medium text-gray-900">
          {field.label}
        </label>
        {showReset && (
          <button
            type="button"
            onClick={() => onChange(resetValue)}
            className="text-[11px] font-medium text-crayoBlue transition-opacity hover:opacity-70"
          >
            Use default
          </button>
        )}
      </div>

      {field.type === "select" ? (
        <Select
          id={field.key}
          value={value}
          options={field.options ?? []}
          ariaLabel={field.label}
          onChange={onChange}
        />
      ) : (
        <div className="flex h-11 items-center rounded-xl border border-[#E7ECF6] bg-white px-3.5 transition-colors focus-within:border-crayoBlue">
          {field.type === "currency" && <span className="text-[14px] text-[#8B93A7]">$</span>}
          <input
            id={field.key}
            type="number"
            inputMode="decimal"
            aria-label={field.label}
            value={Number.isFinite(value) ? value : ""}
            min={field.min}
            max={field.max}
            step={field.step}
            onFocus={(e) => e.target.select()}
            onChange={(e) => onChange(e.target.value === "" ? NaN : Number(e.target.value))}
            onBlur={(e) => {
              const raw = Number(e.target.value);
              onChange(clamp(Number.isFinite(raw) ? raw : field.min ?? 0, field));
            }}
            className="w-full bg-transparent px-1 text-[14px] font-medium text-gray-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          {field.unit && <span className="shrink-0 text-[12.5px] text-[#8B93A7]">{field.unit}</span>}
        </div>
      )}

      {hasSlider && (
        <input
          type="range"
          aria-label={`${field.label} slider`}
          value={sliderValue}
          min={field.min}
          max={field.max}
          step={field.step}
          onChange={(e) => onChange(Number(e.target.value))}
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-[#E7ECF6] accent-crayoBlue"
        />
      )}
    </div>
  );
}

export default function CalculatorWidget({ calculator }: { calculator: Calculator }) {
  const [values, setValues] = useState<CalcValues>(() =>
    Object.fromEntries(calculator.fields.map((f) => [f.key, f.default])),
  );

  const visibleFields = calculator.fields.filter((f) => !f.showIf || f.showIf(values));

  const output = useMemo(() => {
    const safe = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, Number.isFinite(v) ? v : 0]),
    );
    return calculator.compute(safe);
  }, [calculator, values]);

  const setField = (key: string, v: number) =>
    setValues((prev) => {
      const next = { ...prev, [key]: v };
      for (const f of calculator.fields) {
        if (f.syncFrom === key) next[f.key] = v;
      }
      return next;
    });

  const resetValueFor = (field: CalcField) =>
    field.syncFrom != null ? values[field.syncFrom] : field.default;

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_1.05fr] lg:gap-5">
      <div className="rounded-2xl border border-[#E7ECF6] bg-[#FAFBFD] p-5 sm:p-6">
        <div className="flex flex-col gap-5">
          {visibleFields.map((field) => (
            <Field
              key={field.key}
              field={field}
              value={values[field.key]}
              resetValue={resetValueFor(field)}
              onChange={(v) => setField(field.key, v)}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col rounded-2xl border border-crayoBlue/20 bg-crayoBlue/[0.04] p-6 sm:p-7">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-crayoBlue">
          {output.headline.label}
        </span>
        <span className="mt-2 break-words text-[34px] font-semibold leading-[1.05] tracking-tight text-gray-900 [font-variant-numeric:tabular-nums] sm:text-[44px] lg:text-[52px]">
          {output.headline.value}
        </span>
        {output.headline.hint && (
          <span className="mt-2 text-[13px] text-[#5C6678]">{output.headline.hint}</span>
        )}

        <div className="mt-6 flex flex-col divide-y divide-[#E7ECF6] border-t border-[#E7ECF6]">
          {output.stats.map((stat) => (
            <div key={stat.label} className="flex items-baseline justify-between gap-3 py-3">
              <span className="text-[13.5px] text-[#5C6678]">{stat.label}</span>
              <span className="text-right">
                <span className="text-[15px] font-semibold text-gray-900 [font-variant-numeric:tabular-nums]">
                  {stat.value}
                </span>
                {stat.hint && <span className="block text-[11.5px] text-[#5C6678]">{stat.hint}</span>}
              </span>
            </div>
          ))}
        </div>

        {output.note && (
          <p className="mt-5 rounded-xl bg-white p-3.5 text-[12px] leading-[1.5] text-[#5C6678]">
            {output.note}
          </p>
        )}
      </div>
    </div>
  );
}
